import { z } from "zod";

import type { DomainEventName } from "./catalog";

/**
 * TİCARİ OLAY SIRASI (CLOS-884 · KARAR 06 "ticari olay sırası").
 *
 * Kaynak belge: *"Closer Onaylanan Ürün ve Ticari Kararlar"* — KARAR 06.
 *
 * ### Bu dosya ne yapar
 *
 * Bir abonelik akışında **hangi ticari olayın hangisinden sonra gelebileceğini** söyler.
 * Yalnız bunu: yayınlama, teslim ve tüketim kararları burada DEĞİL (`routing.ts` ve
 * CLOS-286). Doğrulayıcı **saf**tır — ne veritabanı okur, ne tabloyu değiştirir. Belgenin
 * `EVENT-AT-05` iddiası (*"Consumer geçici hata → retry"*) ancak saf bir kural için
 * doğrulanabilir: yeniden denenen tüketici ikinci turda farklı bir cevap alsaydı "kaynak
 * gerçek" tüketiciye göre değişirdi.
 *
 * ### ⚠ Tablonun DAYANAĞI — ve dayanağın sınırı
 *
 * Belge **kenar kenar bir sıra tablosu vermiyor.** Verdiği üç şey var ve tablodaki her
 * kenar bunlardan birine dayanıyor; her satırdaki `reason` hangisine dayandığını yazar:
 *
 * 1. `EVENT-AT-01` — *"Doğrulanmış başarılı ödeme → tek PaymentSucceeded → TEK ABONELİK
 *    GEÇİŞİ → tek uygun bildirim."*
 * 2. `EVENT-AT-03` — *"PaymentFailed → grace/restricted kuralları DOĞRU SIRAYLA
 *    çalışmalı."* Belgenin TEK açık zinciri budur.
 * 3. `İlk olay kataloğu` listesinin kendi sırası ve olay adlarının taşıdığı yaşam
 *    döngüsü anlamı (`...Scheduled` → `...Cancelled`, `Low` → `Exhausted`).
 *
 * Bu sınır bilerek yazılı: tablo belgeden TÜRETİLDİ, belgeden KOPYALANMADI. Kart sahibi
 * bir kenarı reddederse değişecek yer tek: aşağıdaki tablo.
 *
 * ### ⚠ Tekrar bir SIRA sorusu değil
 *
 * Hiçbir olay kendisini takip edemez ve tabloda tek bir çift-yönlü kenar yoktur. Belge:
 * *"Duplicate/out-of-order olay tüketimi mükerrer bildirim veya yanlış abonelik state'i
 * oluşturmamalıdır."* Tekrarı sıra tablosunun içine "kendine kenar" olarak koysaydık,
 * idempotency ile sıralamayı tek kavrama sıkıştırmış olurduk: mükerrer bir webhook
 * "geçerli sıra" sayılır ve ikinci bir lifecycle geçişi üretebilirdi.
 */

/**
 * Belgedeki `İlk olay kataloğu` listesi, **belgedeki sırayla**.
 *
 * ⚠ Adlar repository standardına göre revize edildi; belge buna açıkça izin veriyor:
 * *"Kesin teknik isimler repository standardına göre revize edilebilir; SEMANTİK
 * AYRIMLAR KORUNMALIDIR."* Belgedeki `PascalCase` adların hiçbiri kataloğun
 * `alan.olay_adı` biçimine uymuyordu ve iki biçim yan yana yaşasaydı aynı iş olayı için
 * ikinci bir ad doğardı (CLOS-324'ün kapattığı tam da bu).
 *
 * Sıra bir süs değil: belgedeki liste sırası bu ailenin yaşam döngüsü sırasıdır ve
 * `commerceOrder.test.ts` onu belgedeki `PascalCase` adlarla birlikte kilitliyor.
 */
export const COMMERCE_EVENT_NAMES = [
  /** TrialEnding */ "subscription.trial_ending",
  /** TrialEnded */ "subscription.trial_ended",
  /** PaymentSucceeded */ "payment.succeeded",
  /** PaymentFailed */ "payment.failed",
  /** RenewalSucceeded */ "subscription.renewal_succeeded",
  /** RenewalFailed */ "subscription.renewal_failed",
  /** SubscriptionActivated */ "subscription.activated",
  /** SubscriptionPlanChanged */ "subscription.plan_changed",
  /** SubscriptionCancellationScheduled */ "subscription.cancellation_scheduled",
  /** SubscriptionCancelled */ "subscription.cancelled",
  /** GracePeriodStarted */ "subscription.grace_period_started",
  /** RestrictedModeStarted */ "subscription.restricted_mode_started",
  /** EntitlementIncreased */ "entitlement.increased",
  /** EntitlementExhausted */ "entitlement.exhausted",
  /** AiCreditLow */ "ai.credit_low",
  /** AiCreditExhausted */ "ai.credit_exhausted",
  /** SponsoredClientAreaCapacityExhausted */ "client.workspace_capacity_exhausted",
] as const satisfies readonly DomainEventName[];

export type CommerceEventName = (typeof COMMERCE_EVENT_NAMES)[number];

/** Bir geçiş ve GEREKÇESİ. Gerekçesiz bir kenar, savunulamayan bir izindir (`routing.ts` kuralı). */
export type CommerceEventTransition = { next: CommerceEventName; reason: string };

/**
 * Bir akışı BAŞLATABİLEN olaylar.
 *
 * ⚠ Kapalı bir küme ve dar: bir abonelik ya denemeyle ya da ilk ödeme denemesiyle başlar.
 * "Her olay akışı başlatabilir" deseydik sıra kuralı hiçbir şeyi engellemezdi — sıra dışı
 * gelen bir webhook, önceki hiçbir olay görülmemiş gibi işlenip yanlış state üretirdi.
 */
export const COMMERCE_EVENT_ENTRY_POINTS = [
  "subscription.trial_ending",
  "payment.succeeded",
  "payment.failed",
] as const satisfies readonly CommerceEventName[];

/**
 * Sıra tablosu: bir olaydan SONRA hangi olaylar gelebilir.
 *
 * ⚠ Boş dizi TERMİNAL demektir, "unutuldu" değil.
 */
export const COMMERCE_EVENT_ORDER: Record<CommerceEventName, readonly CommerceEventTransition[]> = {
  "subscription.trial_ending": [
    {
      next: "subscription.trial_ended",
      reason: "Belgedeki liste sırası: TrialEnding → TrialEnded; eşik sinyalini bitiş izler.",
    },
    {
      next: "payment.succeeded",
      reason: "Deneme, bitmeden önce ödemeyle sürdürülebilir; bitişi beklemek zorunlu değil.",
    },
  ],
  "subscription.trial_ended": [
    {
      next: "payment.succeeded",
      reason: "Biten deneme ancak doğrulanmış bir ödemeyle aboneliğe döner (EVENT-AT-01).",
    },
  ],
  "payment.succeeded": [
    {
      next: "subscription.activated",
      reason:
        "EVENT-AT-01: 'Doğrulanmış başarılı ödeme → tek PaymentSucceeded → TEK abonelik geçişi.' Tek geçiş, tek kenar.",
    },
  ],
  "payment.failed": [
    {
      next: "subscription.grace_period_started",
      reason:
        "EVENT-AT-03: 'PaymentFailed → grace/restricted kuralları doğru sırayla.' Zincirin ilk halkası ek süredir.",
    },
  ],
  "subscription.renewal_succeeded": [
    {
      next: "entitlement.increased",
      reason: "Yenilenen dönem hakları tazeler; belgedeki 'abonelik ve entitlement' sırası.",
    },
    {
      next: "subscription.plan_changed",
      reason: "Yenilenmiş bir abonelik plan değiştirebilir.",
    },
    {
      next: "subscription.cancellation_scheduled",
      reason: "Yenilenmiş bir abonelik dönem sonuna iptal planlayabilir.",
    },
    {
      next: "subscription.renewal_failed",
      reason: "Bir sonraki dönemin yenilemesi başarısız olabilir.",
    },
  ],
  "subscription.renewal_failed": [
    {
      next: "subscription.grace_period_started",
      reason:
        "Yenileme başarısızlığı ödemesizliğin AYNI kapısından geçer; ikinci bir yol açsaydık biri düzeltilir diğeri unutulurdu.",
    },
  ],
  "subscription.activated": [
    {
      next: "entitlement.increased",
      reason:
        "Belgedeki zorunlu sıra: 'Ödeme, abonelik ve entitlement lifecycle'ı' — hak aktivasyondan sonra doğar.",
    },
    { next: "subscription.plan_changed", reason: "Aktif abonelik plan değiştirebilir." },
    {
      next: "subscription.cancellation_scheduled",
      reason: "Aktif abonelik dönem sonuna iptal planlayabilir.",
    },
    {
      next: "subscription.renewal_succeeded",
      reason: "Aktif aboneliğin dönemi yenilenebilir (liste sırası: Renewal* → Activated ailesi).",
    },
    { next: "subscription.renewal_failed", reason: "Aktif aboneliğin yenilemesi düşebilir." },
  ],
  "subscription.plan_changed": [
    {
      next: "entitlement.increased",
      reason: "Plan değişimi hak tavanını değiştirir; hak olayı plandan SONRA gelir.",
    },
    {
      next: "subscription.cancellation_scheduled",
      reason: "Plan değiştikten sonra iptal planlanabilir.",
    },
    { next: "subscription.renewal_succeeded", reason: "Yeni plan bir sonraki dönemde yenilenir." },
    { next: "subscription.renewal_failed", reason: "Yeni planın yenilemesi düşebilir." },
  ],
  "subscription.cancellation_scheduled": [
    {
      next: "subscription.cancelled",
      reason:
        "Belgedeki liste sırası: SubscriptionCancellationScheduled → SubscriptionCancelled; planlanan kararın dönem sonundaki sonucu.",
    },
  ],
  /** ⚠ TERMİNAL: kapanmış bir abonelik yeniden hareket etmez; yeni abonelik YENİ bir akıştır. */
  "subscription.cancelled": [],
  "subscription.grace_period_started": [
    {
      next: "subscription.restricted_mode_started",
      reason:
        "EVENT-AT-03'ün doğru sırası: kısıtlı mod ancak ek süre DOLDUKTAN sonra başlar. Grace atlanabilseydi kullanıcı düzeltme penceresini hiç görmezdi.",
    },
    {
      next: "payment.succeeded",
      reason: "Ek sürenin varlık sebebi düzeltme; ödeme geldiğinde akış geri döner.",
    },
  ],
  "subscription.restricted_mode_started": [
    {
      next: "payment.succeeded",
      reason:
        "Belge: 'kullanıcı verisi silinmemeli.' Kısıtlı mod geri dönülebilir bir ERİŞİM durumudur; ödeme onu açar.",
    },
    {
      next: "subscription.cancelled",
      reason: "Kısıtlı modda düzelmeyen abonelik dönem sonunda kapanır.",
    },
  ],
  "entitlement.increased": [
    {
      next: "entitlement.exhausted",
      reason:
        "Belgedeki liste sırası: EntitlementIncreased → EntitlementExhausted; verilen hak tükenebilir.",
    },
    { next: "ai.credit_low", reason: "Verilen AI hakkı harcandıkça eşiğe iner." },
    {
      next: "client.workspace_capacity_exhausted",
      reason: "Verilen alan hakkı dolabilir (SponsoredClientAreaCapacityExhausted).",
    },
    { next: "subscription.plan_changed", reason: "Hak alındıktan sonra plan değişebilir." },
    {
      next: "subscription.cancellation_scheduled",
      reason: "Hak alındıktan sonra iptal planlanabilir.",
    },
    { next: "subscription.renewal_succeeded", reason: "Dönem yenilendiğinde hak tazelenir." },
    { next: "subscription.renewal_failed", reason: "Hakkı olan aboneliğin yenilemesi düşebilir." },
  ],
  "entitlement.exhausted": [
    {
      next: "subscription.plan_changed",
      reason: "Tükenen hakkın ürün içindeki çıkışı plan yükseltmektir.",
    },
  ],
  "ai.credit_low": [
    {
      next: "ai.credit_exhausted",
      reason:
        "Belgedeki liste sırası: AiCreditLow → AiCreditExhausted; uyarı eşiği tükenme eşiğinden ÖNCE gelir.",
    },
    {
      next: "entitlement.increased",
      reason: "Kredi eşiğe inince hak satın alınarak artırılabilir.",
    },
  ],
  "ai.credit_exhausted": [
    {
      next: "entitlement.increased",
      reason: "Tükenen kredinin tek çıkışı hakkın artırılmasıdır.",
    },
  ],
  "client.workspace_capacity_exhausted": [
    {
      next: "entitlement.increased",
      reason: "Dolan alan kapasitesi hak artışıyla açılır.",
    },
    {
      next: "subscription.plan_changed",
      reason: "Kapasitenin ürün içindeki diğer çıkışı plan yükseltmektir.",
    },
  ],
};

/** Ad ticari olay ailesinden mi — çalışma anında gelen (kuyruk, HTTP) adlar için. */
export function isCommerceEventName(value: unknown): value is CommerceEventName {
  return typeof value === "string" && (COMMERCE_EVENT_NAMES as readonly string[]).includes(value);
}

/**
 * Sıra kuralı — **saf**: aynı çift her zaman aynı cevabı verir, hiçbir şey değiştirmez.
 *
 * `previous === null` akışın BAŞI demektir; yalnız giriş olayları oradan gelebilir.
 *
 * ⚠ Tekrar (`previous === next`) hiçbir zaman geçerli değildir: tabloda kendine kenar
 * yok. Mükerrer webhook bir sıra sorusu değil bir idempotency sorusudur ve burada
 * "geçerli" sayılsaydı ikinci bir lifecycle geçişi üretebilirdi (EVENT-AT-02).
 */
export function isCommerceEventOrderValid(
  previous: CommerceEventName | null,
  next: CommerceEventName,
): boolean {
  if (previous === null) {
    return (COMMERCE_EVENT_ENTRY_POINTS as readonly string[]).includes(next);
  }
  return COMMERCE_EVENT_ORDER[previous].some((gecis) => gecis.next === next);
}

const TicariOlayAdiSemasi = z.enum(COMMERCE_EVENT_NAMES);

/**
 * Geçişin ŞEMASI. İhlal bir **şema hatası** olarak döner, serbest metin `Error` olarak
 * değil: kod ve alan yolu istemcinin davranış kararını verdiği yerdir, kullanıcıya
 * gösterilecek cümle değil (CLOS-306/CLOS-217).
 *
 * ⚠ Hata `next` alanına asılıyor, kökün kendisine değil: kusurlu olan "çift" değil, o
 * çiftte SIRAYI BOZAN olaydır. Köke asılsaydı çağıran hangi ucun yanlış olduğunu
 * bilemez, iki alanı da reddederdi.
 */
export const CommerceEventTransitionSchema = z
  .object({
    previous: TicariOlayAdiSemasi.nullable(),
    next: TicariOlayAdiSemasi,
  })
  .superRefine((deger, ctx) => {
    if (isCommerceEventOrderValid(deger.previous, deger.next)) return;
    ctx.addIssue({
      code: "custom",
      path: ["next"],
      message:
        deger.previous === null
          ? `Ticari olay akışı "${deger.next}" ile başlayamaz`
          : `Ticari olay sırası ihlali: "${deger.previous}" sonrası "${deger.next}" gelemez`,
      params: { rule: "commerce_event_order", previous: deger.previous, next: deger.next },
    });
  });

export type CommerceEventTransitionInput = z.infer<typeof CommerceEventTransitionSchema>;
